import { intakeHref } from '../lib/referral';
import React, { useEffect, useRef, useState } from 'react';
import { Stethoscope, ClipboardList, Home, Package, LineChart, ShieldCheck, ArrowRight } from 'lucide-react';

const benefits = [
  {
    icon: Stethoscope,
    title: 'Licensed U.S. Providers',
    body: 'Care from qualified, licensed medical professionals in your state.',
  },
  {
    icon: ClipboardList,
    title: 'Personalized Treatment Plan',
    body: 'A plan tailored to your health history, goals, and lifestyle — all online.',
  },
  {
    icon: Home,
    title: '100% Online & Convenient',
    body: 'Complete your consultation, follow-ups, and refills from the comfort of home.',
  },
  {
    icon: Package,
    title: 'Discreet Shipping',
    body: 'Medications delivered directly to your door in plain packaging.',
  },
  {
    icon: LineChart,
    title: 'Ongoing Support',
    body: 'Continuous guidance and check-ins to help you stay on track.',
  },
  {
    icon: ShieldCheck,
    title: 'Clinical & Regulatory Transparency',
    body: 'Treatment may include FDA-approved or compounded medications when determined medically appropriate. Compounded medications are not FDA-approved.',
  },
];

const CANVAS_W = 1440;
const CANVAS_H = 810;

const useCanvasScale = (ref) => {
  const [scale, setScale] = useState(null);
  useEffect(() => {
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      const w = el.getBoundingClientRect().width;
      setScale(window.innerWidth >= 1280 ? w / CANVAS_W : null);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return scale;
};

const BenefitCard = ({ icon: Icon, title, body, i }) => (
  <a
    href={intakeHref()} target="_blank" rel="noopener noreferrer"
    data-testid={`alfa-difference-card-${i + 1}`}
    className="group relative flex flex-col rounded-md bg-white/85 backdrop-blur-md border border-white/70 shadow-[0_10px_30px_-12px_rgba(60,40,20,0.22)] p-[18px] pb-11 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-14px_rgba(60,40,20,0.3)]"
  >
    <span className="h-[46px] w-[46px] rounded-full bg-[color:var(--alfa-sand)] flex items-center justify-center">
      <Icon size={22} strokeWidth={1.5} className="text-[color:var(--alfa-ink)]" />
    </span>
    <h3 className="mt-3 text-[15.5px] font-semibold leading-[1.3] text-[color:var(--alfa-ink)]">{title}</h3>
    <p className="mt-1.5 text-[13.5px] leading-[1.5] text-[color:var(--alfa-muted)]">{body}</p>
    <span className="absolute right-4 bottom-4 h-8 w-8 rounded-full bg-[color:var(--alfa-sand)] flex items-center justify-center transition-colors group-hover:bg-[color:var(--alfa-ink)] group-hover:text-white">
      <ArrowRight size={15} />
    </span>
  </a>
);

const Content = () => (
  <>
    <span
      className="inline-flex items-center uppercase text-[11.5px] tracking-[0.24em] font-medium text-[color:var(--alfa-ink)] px-4 py-2 rounded-full bg-white/90"
      data-testid="alfa-difference-eyebrow"
    >
      The ALFÀ Difference
    </span>
    <h2 className="mt-4 text-[36px] sm:text-[44px] xl:text-[48px] leading-[1.02] tracking-[-0.03em] text-[color:var(--alfa-ink)]">
      What your care<br />
      <span className="text-[color:var(--alfa-royal)]">includes.</span>
    </h2>
    <p className="mt-4 text-[15px] xl:text-[17px] leading-[1.5] text-[color:var(--alfa-ink)] max-w-[680px]">
      Licensed medical care, individualized treatment options, and ongoing support throughout your care journey.
    </p>
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3.5">
      {benefits.map((b, i) => <BenefitCard key={b.title} {...b} i={i} />)}
    </div>
    <p className="mt-3 text-[12px] leading-[1.5] text-[color:var(--alfa-ink)]/80" data-testid="alfa-difference-attribution">
      Clinical care provided by Arora Health &amp; Aesthetics, LLC. Pharmacy fulfillment through VialsRX, subject to state availability and dispensing requirements.
    </p>
  </>
);

export default function AlfaDifference() {
  const frameRef = useRef(null);
  const scale = useCanvasScale(frameRef);
  return (
    <section data-testid="alfa-difference" className="relative w-full px-4 sm:px-6 lg:px-12 py-10 sm:py-14">
      <div ref={frameRef} data-testid="alfa-difference-frame" className="relative max-w-[1440px] mx-auto overflow-hidden rounded-md xl:aspect-[16/9]">
        <img
          src="/images/care-includes-bg.webp"
          alt=""
          aria-hidden="true"
          loading="lazy"
          data-testid="alfa-difference-bg"
          className="w-full aspect-[16/9] object-cover object-left rounded-md xl:rounded-none xl:absolute xl:inset-0 xl:h-full"
        />
        <div aria-hidden="true" className="hidden xl:block absolute inset-0 bg-[linear-gradient(90deg,transparent_30%,rgba(251,247,241,0.45)_45%,rgba(251,247,241,0.6)_100%)]" />
        {scale ? (
          <div
            className="absolute top-0 left-0 origin-top-left flex flex-col justify-center pl-[600px] pr-[40px]"
            style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${scale})` }}
          >
            <div><Content /></div>
          </div>
        ) : (
          <div className="pt-8 px-1 sm:px-2"><Content /></div>
        )}
      </div>
    </section>
  );
}
